/**
 * ProductAssignmentSlideOver
 *
 * Slide-over panel for assigning generated images to product channels.
 * Opens from Studio after image generation to enable drag & drop assignment.
 *
 * @accessibility WCAG 2.1 AA + BFSG konform
 */

import { useState, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  pointerWithin,
} from '@dnd-kit/core';
import type { DragEndEvent, DragStartEvent, UniqueIdentifier } from '@dnd-kit/core';
import { sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { ArrowLeft, Check, X } from 'lucide-react';
import {
  Sheet,
  SheetContent,
  Button,
  Badge,
  Loading,
} from '@/components/v2/ui';
import { GeneratedImagesPanel } from './GeneratedImagesPanel';
import { AssignmentChannelGrid } from './AssignmentChannelGrid';
import { getProductAssets, transferGeneratedImageToProduct } from '@/lib/api/products';
import { cn } from '@/lib/utils';

export interface GeneratedImage {
  assetId: string;
  s3Url?: string;
  width: number;
  height: number;
  generatedAt?: Date;
}

interface SessionInfo {
  timestamp: Date;
  promptSummary?: string;
}

export interface ProductAssignmentSlideOverProps {
  isOpen: boolean;
  onClose: () => void;
  productId: string;
  productName: string;
  productSku?: string;
  generatedImages: GeneratedImage[];
  sessionInfo?: SessionInfo;
  onSaveComplete?: () => void;
}

interface PendingAssignment {
  assetId: string;
  channelId: string;
  channelKey: string;
}

interface DragItemData {
  asset: GeneratedImage;
  fromGenerated: true;
}

export function ProductAssignmentSlideOver({
  isOpen,
  onClose,
  productId,
  productName,
  productSku,
  generatedImages,
  sessionInfo,
  onSaveComplete,
}: ProductAssignmentSlideOverProps) {
  const { t } = useTranslation();
  const [saving, setSaving] = useState(false);
  const [activeId, setActiveId] = useState<UniqueIdentifier | null>(null);
  const [activeDragData, setActiveDragData] = useState<DragItemData | null>(null);
  const [pendingAssignments, setPendingAssignments] = useState<PendingAssignment[]>([]);

  // Fetch existing product assets
  const assetsQuery = useQuery({
    queryKey: ['product-assets', productId],
    queryFn: () => getProductAssets(productId),
    enabled: isOpen && !!productId,
    staleTime: 30_000,
  });

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  // Count assigned images
  const assignedCount = pendingAssignments.length;
  const unassignedCount = generatedImages.length - new Set(pendingAssignments.map(p => p.assetId)).size;

  // Check if image is already assigned to a channel
  const isAssignedToChannel = useCallback((assetId: string, channelId: string) => {
    return pendingAssignments.some(p => p.assetId === assetId && p.channelId === channelId);
  }, [pendingAssignments]);

  const handleDragStart = useCallback((event: DragStartEvent) => {
    setActiveId(event.active.id);
    const data = event.active.data.current as DragItemData | undefined;
    if (data) setActiveDragData(data);
  }, []);

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { over } = event;
    setActiveId(null);
    setActiveDragData(null);

    if (!over || !activeDragData) return;

    const overData = over.data.current as { channelId?: string; channelKey?: string } | undefined;
    if (!overData?.channelId || !overData?.channelKey) return;

    const { channelId, channelKey } = overData;
    const assetId = activeDragData.asset.assetId;

    // Check if already assigned to this channel
    if (isAssignedToChannel(assetId, channelId)) {
      toast.info('Bild bereits diesem Kanal zugewiesen');
      return;
    }

    // Add pending assignment
    setPendingAssignments(prev => [...prev, { assetId, channelId, channelKey }]);
    toast.success(`Bild zu "${channelKey}" hinzugefügt`);
  }, [activeDragData, isAssignedToChannel]);

  const handleAddAllToGlobal = useCallback(() => {
    const globalChannel = assetsQuery.data?.grouped_by_channel?.global?.[0];
    if (!globalChannel) {
      toast.warning('Kein globaler Kanal verfügbar');
      return;
    }
    // This would need the actual global channel ID - for now show info
    toast.info('Funktion in Entwicklung');
  }, [assetsQuery.data]);

  const handleSave = async () => {
    if (pendingAssignments.length === 0) {
      onClose();
      return;
    }

    setSaving(true);
    let successCount = 0;
    let errorCount = 0;

    for (const pending of pendingAssignments) {
      try {
        // Transfer generiertes Bild → erstellt Asset + verknüpft mit Channel
        const result = await transferGeneratedImageToProduct(
          pending.assetId, // generated_image_id
          productId,
          pending.channelId
        );
        if (result.success) {
          successCount++;
        } else {
          console.error('Transfer failed:', result.error);
          errorCount++;
        }
      } catch (err) {
        console.error('Transfer error:', err);
        errorCount++;
      }
    }

    setSaving(false);

    if (successCount > 0 && errorCount === 0) {
      toast.success(`${successCount} Bild${successCount !== 1 ? 'er' : ''} zugewiesen`);
      setPendingAssignments([]);
      onSaveComplete?.();
      onClose();
    } else if (errorCount > 0) {
      toast.error(`${errorCount} Fehler beim Speichern`);
    }
  };

  const handleCancel = () => {
    setPendingAssignments([]);
    onClose();
  };

  return (
    <Sheet open={isOpen} onOpenChange={handleCancel}>
      <SheetContent
        side="right"
        showCloseButton={false}
        className={cn(
          '!w-[85vw] !max-w-[85vw] sm:!max-w-[85vw] p-0',
          'bg-neutral-950 border-neutral-800',
          'flex flex-col'
        )}
      >
        {/* Header */}
        <header className="flex items-center justify-between px-6 py-4 border-b border-neutral-800">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleCancel}
              className="text-neutral-400 hover:text-white"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-lg font-semibold text-white">Bilder zu Produkt hinzufügen</h1>
                <Badge variant="info">{generatedImages.length} neue Bilder</Badge>
              </div>
              <p className="text-sm text-neutral-500 mt-0.5">
                {productName}
                {productSku && (
                  <span className="text-goki-400 ml-2">SKU: {productSku}</span>
                )}
              </p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleCancel}
            className="text-neutral-400 hover:text-red-400"
          >
            <X className="w-5 h-5" />
          </Button>
        </header>

        {/* Main Content with DndContext */}
        <DndContext
          sensors={sensors}
          collisionDetection={pointerWithin}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          <div className="flex-1 flex overflow-hidden">
            {/* Left: Channel Shelves (60%) */}
            <div className="w-[60%] overflow-y-auto p-5 space-y-4 border-r border-neutral-800">
              {assetsQuery.isLoading ? (
                <div className="flex items-center justify-center h-full">
                  <Loading size="lg" label="Kanäle werden geladen..." />
                </div>
              ) : (
                <AssignmentChannelGrid
                  groupedByChannel={assetsQuery.data?.grouped_by_channel || {}}
                  pendingAssignments={pendingAssignments}
                  activeId={activeId}
                />
              )}
            </div>

            {/* Right: Generated Images (40%) */}
            <div className="w-[40%] flex flex-col bg-gradient-to-b from-primary-500/5 to-transparent">
              <GeneratedImagesPanel
                images={generatedImages}
                sessionInfo={sessionInfo}
                onAddAllToGlobal={handleAddAllToGlobal}
                pendingAssignments={pendingAssignments}
              />
            </div>
          </div>

          {/* Drag Overlay - centered on cursor with transform */}
          <DragOverlay adjustScale={false} dropAnimation={null}>
            {activeDragData && (
              <div
                className="rounded-lg border-2 border-primary-500 bg-neutral-800 shadow-2xl overflow-hidden pointer-events-none"
                style={{
                  width: 80,
                  height: 80,
                  // Zentriert um den Cursor: -50% von Breite/Höhe
                  transform: 'translate(-50%, -50%)',
                }}
              >
                <img
                  src={activeDragData.asset.s3Url || `/image/${activeDragData.asset.assetId}`}
                  alt="Dragging"
                  className="w-full h-full object-cover"
                  draggable={false}
                />
              </div>
            )}
          </DragOverlay>
        </DndContext>

        {/* Footer */}
        <footer className="px-6 py-4 border-t border-neutral-800 bg-neutral-900/50">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {assignedCount > 0 && (
                <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
                  <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                  <span className="text-xs font-medium text-emerald-400">
                    {assignedCount} Bild{assignedCount !== 1 ? 'er' : ''} hinzugefügt
                  </span>
                </div>
              )}
              {unassignedCount > 0 && (
                <span className="text-xs text-neutral-500">
                  {unassignedCount} noch nicht zugewiesen
                </span>
              )}
            </div>

            <div className="flex items-center gap-3">
              <Button variant="ghost" onClick={handleCancel} disabled={saving}>
                {t('common.cancel')}
              </Button>
              <Button variant="primary" onClick={handleSave} disabled={saving}>
                {saving ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                    {t('common.saving')}
                  </>
                ) : (
                  <>
                    <Check className="w-4 h-4 mr-2" />
                    Speichern & Schließen
                  </>
                )}
              </Button>
            </div>
          </div>
        </footer>
      </SheetContent>
    </Sheet>
  );
}
